// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import ActionTypes from './action-types';

import BarChart from '../../components/charts/bar';
import colors from '../../styles/colors';
import infoIcon from '../../styles/icons/Dialog-Box-About-24.png';
import Loader from '../../components/loader';
import ExportButton from '../../components/export';

import type { TopExtendedTasksState } from './reducer';
import type { ExtendedTask } from './sagas';
import type { Reducers } from '../../reducers';
import type { Project } from '../../auth/with-project-context';

type Action = {
  type: $Values<typeof ActionTypes> | 'showModal',
  data?: Object,
};
type Dispatch = (action: Action) => void;

type Props = {
  dispatch: Dispatch,
  data: TopExtendedTasksState,
  project: Project,
  week: string,
};

type ChartData = {
  label: string,
  value: number,
  color: string,
  text: string,
};

const styles: Object = {
  // header: {
  //   fontSize: '1.2rem',
  //   textAlign: 'center',
  //   marginBottom: '2rem',
  // },
  widget: {
    border: '1px solid rgb(220, 220, 220)',
    padding: '1rem',
    background: '#fff',
    boxShadow: '0 0 6px 3px rgba(156, 156, 156, 0.1)',
  },
  table: {
    width: '100%',
  },
  taskNameTable: {
    maxWidth: '300px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  // parentHierarchy: {
  //   fontSize: '0.70rem',
  //   marginTop: '0.2rem',
  // },
  noData: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px',
  },
};

/* eslint-disable */
const getHeight: Object = {
  '1': '140',
  '2': '175',
  '3': '207',
  '4': '240',
  '5': '270',
  '6': '302',
  '7': '334',
  '8': '366',
  '9': '398',
  '10': '430',
};
/* eslint-enable */

class TopExtendedTasks extends React.PureComponent<Props> {
  componentDidMount() {
    if (this.props.week !== '') {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.week !== '' && prevProps.week !== this.props.week) {
      this.fetchData();
    }
  }

  getData(): ChartData[] {
    return this.props.data.chartData.map(
      (task: ExtendedTask): ChartData => ({
        label: task.name,
        value: task.ratio * 100,
        color: colors.themeSecondaryColor,
        text:
          `${task.name}<br>Actual duration: ${task.actual_duration}<br>`
          + `Base duration: ${
            task.base_duration
          }<br>Extension: ${task.actual_duration - task.base_duration}`,
      }),
    );
  }

  showTaskDetailModal = (task: Object) => {
    this.props.dispatch({
      type: 'showModal',
      modalType: 'taskDetail',
      modalProps: {
        activityId: task.activity_id,
        taskId: task.id,
        taskName: task.name,
      },
    });
  };

  fetchData() {
    this.props.dispatch({
      type: ActionTypes.GET_TOP_EXTENDED_TASKS,
      data: {
        projectId: this.props.project.id,
        startDate: this.props.week,
      },
    });
  }

  renderTable(): React.Element<any> {
    return (
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.taskNameTable}>Task</th>
            <th className="hide-element">Base Duration (days)</th>
            <th className="hide-element">Actual Duration (days)</th>
          </tr>
        </thead>
        <tbody>
          {this.props.data.chartData.map(
            (task: ExtendedTask, i: number): React.Node => (
              <tr key={i}>
                <td style={styles.taskNameTable}>
                  <a
                    onClick={this.showTaskDetailModal.bind(this, task)}
                    className="underline-hover"
                  >
                    {task.name}
                  </a>
                </td>
                <td className="hide-element">{task.base_duration}</td>
                <td className="hide-element">{task.actual_duration}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    );
  }

  renderWidget(): React$Element<*> {
    const options: Object = {
      height: getHeight[this.props.data.chartData.length],
      yaxis: {
        showticklabels: false,
      },
      xaxis: {
        title: 'Percentage Extended',
      },
      margin: {
        t: 35,
        r: 10,
        l: 3,
      },
    };
    if (this.props.data.chartData.length === 0 && this.props.data.loading === false) {
      return (
        <span style={styles.noData}>
        No data available
        </span>
      );
    }
    return (
      <div>
        <ExportButton projectId={this.props.project.id} startDate={this.props.week} widgetName="top_extended_tasks" />
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ width: '60%' }}>
            {this.renderTable()}
          </div>
          <div style={{ width: '40%' }}>
            <BarChart data={this.getData()} options={options} dataOptions={{ orientation: 'h' }} />
          </div>
        </div>
      </div>
    );
  }

  render(): React.Node {
    /* eslint max-len: ["error", { "ignoreStrings": true, "code": 150}] */
    return (
      <section style={styles.widget}>
        <h1 className="header-widget-common">
          Top Extended Tasks
          <img
            data-tip="This chart lists the top extended tasks sorted based on thier extended durations from the base duration"
            width="14"
            src={infoIcon}
            alt="i"
          />
        </h1>
        <ReactTooltip place="top" type="dark" effect="solid" border multiline />
        {
          this.props.data.loading === null ? <Loader /> : this.renderWidget()
        }
      </section>
    );
  }
}

const component: any = connect(
  (state: Reducers): { data: TopExtendedTasksState } => ({
    data: state.topExtendedTasks,
  }),
)(TopExtendedTasks);

component.style = {
  width: '98%',
};

export default component;
